// Controllers Index
// Registers both core package controllers and app-specific controllers

import { application } from "controllers/application"

// ========================================
// Core Package Controllers (from @core)
// ========================================
import {
  DataTableController,
  DataTablePerPageController,
  DatepickerController,
  AutocompleteController,
  FormValidationController,
  DependsOnController,
  NestedFieldsController,
  ColorPickerController,
  PasswordToggleController,
  OtpInputController,
  SortableController,
  TooltipController,
  FlashController,
  ModalController,
  ModalAutoOpenController,
  ModalLinkController,
  CollapseController,
  DarkModeController,
  FabButtonController,
  CopyLinkController,
  CodeClipboardController,
  UploaderController,
  RemoteJsController
} from '@core/controllers'

// Register core controllers
application.register("data-table", DataTableController)
application.register("data-table-per-page", DataTablePerPageController)
application.register("datepicker", DatepickerController)
application.register("autocomplete", AutocompleteController)
application.register("form-validation", FormValidationController)
application.register("depends-on", DependsOnController)
application.register("nested-fields", NestedFieldsController)
application.register("color-picker", ColorPickerController)
application.register("password-toggle", PasswordToggleController)
application.register("otp-input", OtpInputController)
application.register("sortable", SortableController)
application.register("tooltip", TooltipController)
application.register("flash", FlashController)
application.register("modal", ModalController)
application.register("modal-auto-open", ModalAutoOpenController)
application.register("modal-link", ModalLinkController)
application.register("collapse", CollapseController)
application.register("dark-mode", DarkModeController)
application.register("fab-button", FabButtonController)
application.register("copy-link", CopyLinkController)
application.register("code-clipboard", CodeClipboardController)
application.register("uploader", UploaderController)
application.register("remote-js", RemoteJsController)

// ========================================
// App-Specific Controllers
// ========================================

// Number formatter (thousands separators in text inputs)
import NumberFormatterController from "controllers/number_formatter_controller"
application.register("number-formatter", NumberFormatterController)

// Attachment controllers
import AttachmentProcessController from "controllers/attachment_process_controller"
application.register("attachment-process", AttachmentProcessController)

import AttachmentValidationController from "controllers/attachment_validation_controller"
application.register("attachment-validation", AttachmentValidationController)

// Report controllers
import ReportController from "controllers/report_controller"
application.register("report", ReportController)

import ReportRestorationController from "controllers/report_restoration_controller"
application.register("report-restoration", ReportRestorationController)

import ReportDuplicatorController from "controllers/report_duplicator_controller"
application.register("report-duplicator", ReportDuplicatorController)

import ReportColumnsSortableController from "controllers/report_columns_sortable_controller"
application.register("report-columns-sortable", ReportColumnsSortableController)

import SheetListSortableController from "controllers/sheet_list_sortable_controller"
application.register("sheet-list-sortable", SheetListSortableController)

import ReportDownloadController from "controllers/report_download_controller"
application.register("report-download", ReportDownloadController)

import ReportAutosaveController from "controllers/report_autosave_controller"
application.register("report-autosave", ReportAutosaveController)

// Report Designer controllers
import ColumnActionsController from "controllers/column_actions_controller"
application.register("column-actions", ColumnActionsController)

import ColumnSearchV3Controller from "controllers/column_search_v3_controller"
application.register("column-search-v3", ColumnSearchV3Controller)

import ColumnSelectorPanelController from "controllers/column_selector_panel_controller"
application.register("column-selector-panel", ColumnSelectorPanelController)

import ColumnEditorV3Controller from "controllers/column_editor_v3_controller"
application.register("column-editor-v3", ColumnEditorV3Controller)

import PropertiesSidepanelController from "controllers/properties_sidepanel_controller"
application.register("properties-sidepanel", PropertiesSidepanelController)

import ReportDesignerLayoutController from "controllers/report_designer_layout_controller"
application.register("report-designer-layout", ReportDesignerLayoutController)

import ReportFormV3Controller from "controllers/report-form-v3-controller"
application.register("report-form-v3", ReportFormV3Controller)

import ColumnPropertiesEditorController from "controllers/column_properties_editor_controller"
application.register("column-properties-editor", ColumnPropertiesEditorController)

import ColumnLibraryCollapseController from "controllers/column_library_collapse_controller"
application.register("column-library-collapse", ColumnLibraryCollapseController)

import SelectedColumnsController from "controllers/selected_columns_controller"
application.register("selected-columns", SelectedColumnsController)

import LockedCellsWarningController from "controllers/locked_cells_warning_controller"
application.register("locked-cells-warning", LockedCellsWarningController)

// Zip/Export controllers
import ZipExportController from "controllers/zip_export_controller"
application.register("zip-export", ZipExportController)

import ZipExportRestorationController from "controllers/zip_export_restoration_controller"
application.register("zip-export-restoration", ZipExportRestorationController)

import BatchCopyProgressController from "controllers/batch_copy_progress_controller"
application.register("batch-copy-progress", BatchCopyProgressController)

import FeedbackReportController from "controllers/feedback_report_controller"
application.register("feedback-report", FeedbackReportController)

import UnifiedExportController from "controllers/unified_export_controller"
application.register("unified-export", UnifiedExportController)

// Developer controllers
import AttachmentTypeDuplicatorController from "controllers/attachment_type_duplicator_controller"
application.register("attachment-type-duplicator", AttachmentTypeDuplicatorController)

import FormulaInputController from "controllers/formula_input_controller"
application.register("formula-input", FormulaInputController)

import ValidationBuilderController from "controllers/validation_builder_controller"
application.register("validation-builder", ValidationBuilderController)

import ProcessorSelectorController from "controllers/processor_selector_controller"
application.register("processor-selector", ProcessorSelectorController)

import TemplateTypeSelectorController from "controllers/template_type_selector_controller"
application.register("template-type-selector", TemplateTypeSelectorController)

// UI controllers
import AutoModalController from "controllers/auto_modal_controller"
application.register("auto-modal", AutoModalController)

import FieldFormController from "controllers/field_form_controller"
application.register("field-form", FieldFormController)

import FileInputController from "controllers/file_input_controller"
application.register("file-input", FileInputController)

import GlobalRoleGuardController from "controllers/global_role_guard_controller"
application.register("global-role-guard", GlobalRoleGuardController)

import ColorPreviewDropController from "controllers/color_preview_drop_controller"
application.register("color-preview-drop", ColorPreviewDropController)

import SidebarController from "controllers/sidebar_controller"
application.register("sidebar", SidebarController)

import SecondaryNavSubmenuController from "controllers/secondary_nav_submenu_controller"
application.register("secondary-nav-submenu", SecondaryNavSubmenuController)

import PageTitleController from "controllers/page_title_controller"
application.register("page-title", PageTitleController)

import TurboStreamLinkController from "controllers/turbo_stream_link_controller"
application.register("turbo-stream-link", TurboStreamLinkController)

import TrixEditorController from "controllers/trix_editor_controller"
application.register("trix-editor", TrixEditorController)

import HierarchicalCheckboxController from "controllers/hierarchical_checkbox_controller"
application.register("hierarchical-checkbox", HierarchicalCheckboxController)

import HeaderTabsScrollController from "controllers/header_tabs_scroll_controller"
application.register("header-tabs-scroll", HeaderTabsScrollController)

import ParentCollapseController from "controllers/parent_collapse_controller"
application.register("parent-collapse", ParentCollapseController)

import DataFieldSchemaManagerController from "controllers/data_field_schema_manager_controller"
application.register("data-field-schema-manager", DataFieldSchemaManagerController)

import HashCollapseController from "controllers/hash_collapse_controller"
application.register("hash-collapse", HashCollapseController)

import TabHashController from "controllers/tab_hash_controller"
application.register("tab-hash", TabHashController)

import TableDropdownController from "controllers/table_dropdown_controller"
application.register("table-dropdown", TableDropdownController)

import SecondaryNavMobileController from "controllers/secondary_nav_mobile_controller"
application.register("secondary-nav-mobile", SecondaryNavMobileController)

import TargetableController from "controllers/targetable_controller"
application.register("targetable", TargetableController)

import BalanceCalculatorController from "controllers/balance_calculator_controller"
application.register("balance-calculator", BalanceCalculatorController)

import GlobalSearchController from "controllers/global_search_controller"
application.register("global-search", GlobalSearchController)
